import { normalizeClientHost } from '~/utils/shopDomain'

function stripTrailingSlash(value: string): string {
  return value.replace(/\/$/, '')
}

/** Drop `/{shopSlug}` from a route path so public URLs stay storefront-relative. */
export function storefrontRelativePath(fullPath: string, shopSlug: string): string {
  const path = fullPath || '/'
  const slug = shopSlug.trim().toLowerCase()
  if (!slug) return path
  const prefix = `/${slug}`
  if (path === prefix) return '/'
  if (
    path.startsWith(`${prefix}/`)
    || path.startsWith(`${prefix}?`)
    || path.startsWith(`${prefix}#`)
  ) {
    return path.slice(prefix.length) || '/'
  }
  return path
}

export function joinStorefrontUrl(root: string, path = '/'): string {
  const base = stripTrailingSlash(root)
  if (!path || path === '/') return base || '/'
  const suffix = path.startsWith('/') ? path : `/${path}`
  return `${base}${suffix}`
}

/**
 * Public shop origin for share / canonical / OG links.
 * Custom domain when set; otherwise platform URL + /{slug}.
 */
export function useShopStorefrontRoot() {
  const config = useRuntimeConfig()
  const shopSlug = useShopSlug()
  const { customDomainMode, domainShop } = useShopDomain()
  const settings = useState<Record<string, string>>('site-settings', () => ({}))

  let requestOrigin = ''
  if (import.meta.server) {
    try {
      requestOrigin = stripTrailingSlash(useRequestURL().origin)
    }
    catch {
      requestOrigin = ''
    }
  }

  return computed(() => {
    const fromSettings = stripTrailingSlash(settings.value.storefront_url || '')
    if (fromSettings) return fromSettings

    const custom = normalizeClientHost(domainShop.value?.customDomain || '')
    if (custom) return `https://${custom}`

    if (customDomainMode.value) {
      if (requestOrigin) return requestOrigin
      if (import.meta.client) return stripTrailingSlash(window.location.origin)
    }

    const platform = stripTrailingSlash(String(config.public.siteUrl || ''))
    const slug = shopSlug.value
    return slug ? `${platform}/${slug}` : platform
  })
}

export function useShopPublicUrl() {
  const root = useShopStorefrontRoot()
  return (path = '/') => joinStorefrontUrl(root.value, path)
}
